import React, { useState } from 'react';
import { usePlaybook } from '../../context/PlaybookContext';
import SafeIcon from '../../common/SafeIcon';
import { motion } from 'framer-motion';
import { formatDate } from '../../utils/helpers';

const LearningCenter = ({ projectId }) => {
  const { learningModules, createLearningModule, scenarios, battlecards } = usePlaybook();
  const [isCreating, setIsCreating] = useState(false);
  const [newModuleData, setNewModuleData] = useState({ title: '', source: 'Scenario', sourceId: '' });

  const projectModules = learningModules.filter(m => m.project_id === projectId);
  
  // Available sources for conversion
  const availableScenarios = scenarios.filter(s => s.project_id === projectId);
  const availableBattlecards = battlecards.filter(b => b.project_id === projectId);

  const handleCreate = () => {
    if (!newModuleData.title) return;
    
    let objectives = "Lær å mestre dette emnet.";
    if (newModuleData.source === 'Scenario') {
      const s = availableScenarios.find(x => x.id === newModuleData.sourceId);
      if (s) objectives = `Mål: ${s.goal}\n\nNøkkelpunkter: \n${s.checklist}`;
    } else if (newModuleData.source === 'Battlecard') {
      const b = availableBattlecards.find(x => x.id === newModuleData.sourceId);
      if (b) objectives = `Lær å håndtere ${b.competitor_name}.\n\nFokus: ${b.positioning_vs}`;
    }

    createLearningModule({
      project_id: projectId,
      title: newModuleData.title,
      content_source: newModuleData.source,
      source_id: newModuleData.sourceId,
      learning_objectives: objectives
    });
    
    setIsCreating(false);
    setNewModuleData({ title: '', source: 'Scenario', sourceId: '' });
  };

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-xl font-black text-slate-900">E-læring & Moduler</h3>
          <p className="text-sm text-slate-500">Gjør playbooken om til interaktiv trening.</p>
        </div>
        <button 
          onClick={() => setIsCreating(true)} 
          className="flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-xl font-black text-xs uppercase tracking-widest hover:bg-slate-800 transition-all shadow-lg"
        >
          <SafeIcon icon="🎓" /> Opprett Modul
        </button>
      </div>

      {isCreating && (
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="bg-slate-50 p-6 rounded-2xl border border-slate-200 shadow-inner mb-8">
          <h4 className="text-sm font-black text-slate-900 uppercase tracking-widest mb-4">Ny Læringsmodul</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Tittel</label>
              <input 
                value={newModuleData.title} 
                onChange={e => setNewModuleData({ ...newModuleData, title: e.target.value })}
                className="w-full p-3 border border-slate-200 rounded-xl text-sm font-bold outline-none focus:ring-2 focus:ring-primary-500" 
                placeholder="F.eks. Mestring av Discovery"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Kildeinnhold</label>
              <div className="flex gap-2 mb-2">
                {['Scenario', 'Battlecard', 'Onboarding'].map(type => (
                  <button 
                    key={type}
                    onClick={() => setNewModuleData({ ...newModuleData, source: type, sourceId: '' })}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-all ${newModuleData.source === type ? 'bg-white border-primary-500 text-primary-700 shadow-sm' : 'border-transparent text-slate-500 hover:bg-slate-200'}`}
                  >
                    {type}
                  </button>
                ))}
              </div>
              <select 
                value={newModuleData.sourceId} 
                onChange={e => setNewModuleData({ ...newModuleData, sourceId: e.target.value })}
                className="w-full p-3 bg-white border border-slate-200 rounded-xl text-sm outline-none"
                disabled={newModuleData.source === 'Onboarding'} // Simple demo logic
              >
                <option value="">Velg spesifikt innhold...</option>
                {newModuleData.source === 'Scenario' && availableScenarios.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
                {newModuleData.source === 'Battlecard' && availableBattlecards.map(b => (
                  <option key={b.id} value={b.id}>{b.competitor_name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <button onClick={() => setIsCreating(false)} className="px-4 py-2 text-slate-500 font-bold text-xs hover:bg-slate-200 rounded-lg">Avbryt</button>
            <button onClick={handleCreate} disabled={!newModuleData.title} className="px-6 py-2 bg-emerald-500 text-white rounded-lg font-bold text-xs uppercase tracking-widest hover:bg-emerald-600 disabled:opacity-50 shadow-md">
              Lagre Modul
            </button>
          </div>
        </motion.div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {projectModules.map(module => (
          <div key={module.plm_id} className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm hover:shadow-md transition-all group relative overflow-hidden">
            <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
              <SafeIcon icon="🎓" className="text-6xl text-slate-900" />
            </div>
            
            <div className="flex justify-between items-start mb-4">
              <span className={`px-2 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest border ${module.content_source === 'Scenario' ? 'bg-blue-50 text-blue-700 border-blue-100' : 'bg-rose-50 text-rose-700 border-rose-100'}`}>
                {module.content_source}
              </span>
              {module.scorm_ready && (
                <span className="text-[10px] font-bold text-emerald-600 flex items-center gap-1 bg-emerald-50 px-2 py-1 rounded-full">
                  <SafeIcon icon="✅" className="w-3 h-3" /> SCORM
                </span>
              )}
            </div>

            <h4 className="text-lg font-black text-slate-900 mb-2">{module.title}</h4>
            <p className="text-sm text-slate-500 font-medium line-clamp-3 mb-6 min-h-[60px]">
              {module.learning_objectives}
            </p>

            <div className="pt-4 border-t border-slate-100 flex justify-between items-center">
              <span className="text-[10px] font-bold text-slate-400">Opprettet: {formatDate(module.created_at)}</span>
              <button className="text-primary-600 font-bold text-xs hover:underline flex items-center gap-1">
                Start <SafeIcon icon="➡️" className="w-3 h-3" />
              </button>
            </div>
          </div>
        ))}
        
        {projectModules.length === 0 && !isCreating && (
          <div className="col-span-full py-16 text-center border-2 border-dashed border-slate-200 rounded-[2rem]">
            <SafeIcon icon="📚" className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 font-medium">Ingen moduler opprettet.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LearningCenter;